import { NavLink, Outlet } from 'react-router-dom';
import { useQuery } from '@tanstack/react-query';
import { useAuth } from '@shared/auth/useAuth';
import { listSupportSites, SupportApiError } from '../api/supportClient';

const TABS = [
  { to: 'inventory', label: 'Inventory' },
  { to: 'diagnose', label: 'Diagnose' },
  { to: 'tickets', label: 'Tickets' },
  { to: 'act', label: 'Act' },
  { to: 'knowledge', label: 'Knowledge' },
  { to: 'audit', label: 'Audit' },
  { to: 'metrics', label: 'Metrics' },
];

/**
 * Plan, Step 7: "distinguish 'core tier (broker) unreachable' from
 * 'web tier degraded' in the UI rather than rendering empty." This
 * console IS the web tier's own UI, so "web tier degraded" here
 * specifically means "the support api-server itself is unreachable or
 * erroring" -- caught here, once, at the shell level, rather than each
 * pane independently discovering the same outage. Per-SITE core-tier
 * (broker) reachability is a narrower, per-row concern and is surfaced
 * in `InventoryPane` instead (a broker being down for one site is not
 * a reason to blank the whole console).
 */
function useWebTierHealth() {
  const query = useQuery({
    queryKey: ['support', 'web-tier-health'],
    // Must stay a CHEAP endpoint: this fires every 30s for the whole
    // session purely to answer "is the support API reachable". When
    // `/support/sites` briefly also carried broker-sampled CPU/memory,
    // this liveness poll silently inherited a ~2s fan-out to every
    // site's broker -- work it has no use for. `/site-health` is where
    // that lives now.
    queryFn: listSupportSites,
    retry: 1,
    refetchInterval: 30_000,
  });
  const degraded =
    query.isError &&
    !(query.error instanceof SupportApiError && query.error.status >= 400 && query.error.status < 500);
  return { degraded, error: query.error };
}

export function ConsoleShell() {
  const { user, roles, logout } = useAuth();
  const { degraded } = useWebTierHealth();

  return (
    // `height` (not `minHeight`) is load-bearing: `#root` is already
    // bounded to `height: 100%` (styles/global.css), but `minHeight`
    // here would let this element grow past it anyway, and then
    // nothing downstream (main's `flex: 1; minHeight: 0`, Diagnose's
    // own internal scroll box) has a fixed height to shrink against --
    // the exact bug the operator SPA hit and fixed the same way
    // (`nexsoss_rmf-web/src/shell/AppShell.css`'s own comment on
    // `.nx-shell`).
    <div style={{ height: '100%', display: 'flex', flexDirection: 'column' }}>
      <header
        style={{
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'space-between',
          padding: '12px 20px',
          borderBottom: '1px solid var(--nx-border)',
          background: 'var(--nx-bg2)',
        }}
      >
        <div style={{ display: 'flex', alignItems: 'center', gap: 24 }}>
          <strong
            style={{
              fontFamily: 'var(--nx-ff-h)',
              letterSpacing: '0.14em',
              background: 'var(--nx-grad)',
              WebkitBackgroundClip: 'text',
              backgroundClip: 'text',
              color: 'transparent',
            }}
          >
            NEXSOSS PLATFORM SUPPORT
          </strong>
          <nav style={{ display: 'flex', gap: 4 }}>
            {TABS.map((tab) => (
              <NavLink
                key={tab.to}
                to={tab.to}
                style={({ isActive }) => ({
                  padding: '6px 14px',
                  borderRadius: 'var(--nx-r)',
                  textDecoration: 'none',
                  color: isActive ? 'var(--nx-white)' : 'var(--nx-muted)',
                  background: isActive ? 'var(--nx-bg3)' : 'transparent',
                })}
              >
                {tab.label}
              </NavLink>
            ))}
          </nav>
        </div>
        <div style={{ display: 'flex', alignItems: 'center', gap: 12, fontSize: 13 }}>
          <span style={{ color: 'var(--nx-muted)' }}>
            {user?.displayName} · {roles.map((r) => r.replace('nexsoss:', '')).join(', ')}
          </span>
          <button type="button" onClick={() => void logout()}>
            Sign out
          </button>
        </div>
      </header>

      {degraded && (
        <div
          role="alert"
          style={{
            padding: '8px 20px',
            background: 'var(--nx-error)',
            color: 'var(--nx-white)',
          }}
        >
          Web tier degraded: the support API is unreachable or erroring. Site/component data below
          may be stale or missing. This is distinct from an individual site's core tier (broker)
          being unreachable, which is shown per-site in Inventory.
        </div>
      )}

      {/* `overflow: auto` here is the fallback for panes that don't
          opt into their own internal scroll box (Inventory, Act,
          Knowledge, Audit) -- they keep scrolling exactly as before.
          Diagnose opts out of this by filling the available height
          itself (`.nx-diagnose { height: 100% }`) and scrolling only
          its issue table internally, so this element never needs to
          scroll for that pane. */}
      <main style={{ flex: 1, minHeight: 0, padding: 20, overflow: 'auto' }}>
        <Outlet />
      </main>
    </div>
  );
}
